/**
 * Erika Morón — product rails.
 *  <em-rail>          horizontal product row: prev/next arrows (disabled near the edges), scroll progress bar.
 *  <em-rail-tabs>     tabbed collections: roving tabindex, arrow keys, fade between panels, editor block select.
 *  <em-related>       loads related products from the recommendations endpoint when not server-rendered.
 */

const prefersMotion = () => !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const EDGE = 100;

class EmRail extends HTMLElement {
  #abort;
  #resizeObserver;
  #frame = 0;

  connectedCallback() {
    this.track = this.querySelector('[data-em-rail-track]');
    if (!this.track) return;

    this.prev = this.querySelector('[data-em-rail-step="-1"]');
    this.next = this.querySelector('[data-em-rail-step="1"]');
    this.progress = this.querySelector('[data-em-rail-progress]');

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.track.addEventListener('scroll', this.#schedule, { passive: true, signal });
    this.prev?.addEventListener('click', () => this.#page(-1), { signal });
    this.next?.addEventListener('click', () => this.#page(1), { signal });

    this.#resizeObserver = new ResizeObserver(this.#schedule);
    this.#resizeObserver.observe(this.track);
    this.#update();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#resizeObserver?.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  #schedule = () => {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#update();
    });
  };

  #page(direction) {
    const card = [...this.track.children].find((child) => child.offsetWidth > 0);
    const distance = card ? card.getBoundingClientRect().width : this.track.clientWidth;
    this.track.scrollBy({ left: direction * distance, behavior: prefersMotion() ? 'smooth' : 'instant' });
  }

  #update() {
    const max = this.track.scrollWidth - this.track.clientWidth;
    const scrollable = max > 1;
    const position = Math.abs(this.track.scrollLeft);

    this.classList.toggle('is-scrollable', scrollable);

    [this.prev, this.next].forEach((button) => {
      if (button) button.hidden = !scrollable;
    });
    if (this.prev) this.prev.disabled = position < EDGE;
    if (this.next) this.next.disabled = max - position < EDGE;

    if (this.progress && this.track.scrollWidth > 0) {
      const visible = this.track.clientWidth / this.track.scrollWidth;
      const ratio = max > 0 ? position / max : 1;
      this.progress.style.setProperty('--em-rail-progress', String(visible + ratio * (1 - visible)));
    }
  }
}

class EmRailTabs extends HTMLElement {
  #abort;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.addEventListener('click', (event) => {
      const tab = event.target instanceof Element ? event.target.closest('button[role="tab"]') : null;
      if (tab) this.select(this.tabs.indexOf(tab));
    }, { signal });

    this.addEventListener('keydown', (event) => {
      const index = this.tabs.indexOf(document.activeElement);
      if (index < 0 || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      const next = (index + (event.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
      this.select(next);
      this.tabs[next].focus();
    }, { signal });

    this.addEventListener('shopify:block:select', (event) => {
      const index = this.tabs.indexOf(event.target);
      if (index >= 0) this.select(index, true);
    }, { signal });
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  get tabs() {
    return [...this.querySelectorAll('button[role="tab"]')];
  }

  async select(index, instant = false) {
    const tabs = this.tabs;
    const tab = tabs[index];
    if (!tab || tab.getAttribute('aria-selected') === 'true') return;

    const current = tabs.find((item) => item.getAttribute('aria-selected') === 'true');
    const fromPanel = current ? document.getElementById(current.getAttribute('aria-controls')) : null;
    const toPanel = document.getElementById(tab.getAttribute('aria-controls'));

    tabs.forEach((item) => {
      const active = item === tab;
      item.setAttribute('aria-selected', String(active));
      item.tabIndex = active ? 0 : -1;
    });

    const list = tab.parentElement;
    if (list && list.scrollWidth > list.clientWidth) {
      list.scrollTo({ left: tab.offsetLeft - list.clientWidth / 2 + tab.offsetWidth / 2, behavior: prefersMotion() ? 'smooth' : 'instant' });
    }

    const link = toPanel?.dataset.url;
    if (link) this.querySelectorAll('[data-em-view-all]').forEach((anchor) => anchor.setAttribute('href', link));

    if (!toPanel || fromPanel === toPanel) return;

    if (fromPanel && !instant && prefersMotion()) {
      await fromPanel.animate({ opacity: [1, 0], transform: ['translateY(0)', 'translateY(10px)'] }, { duration: 150 }).finished.catch(() => {});
    }
    if (fromPanel) fromPanel.hidden = true;
    toPanel.hidden = false;
    if (!instant && prefersMotion()) {
      toPanel.animate({ opacity: [0, 1], transform: ['translateY(10px)', 'translateY(0)'] }, { duration: 150 });
    }
  }
}

class EmRelated extends HTMLElement {
  connectedCallback() {
    if (this.querySelector('[data-em-related-ready]') || !this.dataset.url || this.dataset.loading) return;
    this.dataset.loading = 'true';

    fetch(this.dataset.url)
      .then((response) => response.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector(`em-related[data-section-id="${CSS.escape(this.dataset.sectionId || '')}"]`);
        if (fresh?.querySelector('[data-em-related-ready]')) this.replaceChildren(...fresh.childNodes);
      })
      .catch(() => {})
      .finally(() => delete this.dataset.loading);
  }
}

for (const [name, constructor] of [
  ['em-rail', EmRail],
  ['em-rail-tabs', EmRailTabs],
  ['em-related', EmRelated],
]) {
  if (!customElements.get(name)) customElements.define(name, constructor);
}
